import type { CSSProperties, FC, ReactNode } from 'react'

import {
    ArrowLeftRight,
    Building2,
    Download,
    Globe,
    ListChecks,
    Webhook
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button'
import { RevealText } from '@/components/generals/reveal-text'
import { PageHero } from '@/components/sections/page-hero'
import { SplitFeature } from '@/components/sections/split-feature'
import { FeaturePreview } from '@/components/features/feature-preview'
import { FEATURES } from '@/constants/features'

const TONES = ['burgundy', 'cream'] as const

const CAPABILITIES = [
    { title: 'Approval workflows', body: 'Route bills, budgets and exceptions to the right approver by amount and category.', Icon: ListChecks },
    { title: 'Multi-entity', body: 'Consolidate subsidiaries and see each entity on its own or as one company.', Icon: Building2 },
    { title: 'Multi-currency', body: 'Track balances in any currency with daily rates and clean conversion.', Icon: Globe },
    { title: 'Two-way sync', body: 'Categories and journal entries flow back to your accounting system automatically.', Icon: ArrowLeftRight },
    { title: 'Exports anywhere', body: 'Send any report or table to PDF, Excel, CSV or a live Google Sheet.', Icon: Download },
    { title: 'API and webhooks', body: 'Pull clean, reconciled data into your own tools and data warehouse.', Icon: Webhook }
]

const Features: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Features'
                description='Automated reporting, real-time expense tracking, AI insights, cash flow forecasting, revenue analytics and smart budgets. See everything Veyra does for your finance team.'
            />

            <PageHero
                eyebrow='Features'
                title={<>Everything you need to <span className='text-stone'>understand</span> your finances.</>}
                description='Six capabilities built on one reconciled source of truth. Every report, forecast and insight agrees with every other, because they all start from the same numbers.'
                actions={(
                    <>
                        <ButtonLink href='/signup' size='lg' arrow>Start free trial</ButtonLink>
                        <ButtonLink href='/demo' variant='secondary' size='lg'>Request demo</ButtonLink>
                    </>
                )}
            >
                <nav aria-label='Feature list'>
                    <ol className='grid border-l border-t border-burgundy/10 sm:grid-cols-2 lg:grid-cols-3'>
                        {FEATURES.map(({ id, number, title, Icon }) => (
                            <li key={id} className='border-b border-r border-burgundy/10'>
                                <a href={`#${id}`} className='group flex items-center gap-4 p-5 transition-colors duration-300 hover:bg-ivory/70'>
                                    <span className='font-mono text-xs text-stone'>{number}</span>
                                    <span className='flex size-9 items-center justify-center rounded-full bg-burgundy text-gold'>
                                        <Icon aria-hidden='true' className='size-4' />
                                    </span>
                                    <span className='text-[15px] font-medium text-burgundy'>{title}</span>
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>
            </PageHero>

            {FEATURES.map((feature, index) => (
                <SplitFeature
                    key={feature.id}
                    id={feature.id}
                    label={`${feature.number}  ${feature.title}`}
                    title={feature.headline}
                    description={(
                        <>
                            <p>{feature.summary}</p>
                            <p className='mt-4 text-base opacity-90'>{feature.details}</p>
                        </>
                    )}
                    points={feature.points}
                    visual={<FeaturePreview feature={feature.id} />}
                    reverse={index % 2 === 1}
                    tone={TONES[index % 2]}
                />
            ))}

            <section aria-labelledby='capabilities-title' className='bg-paper'>
                <Container className='py-28 md:py-36'>
                    <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                        <div className='lg:col-span-7'>
                            <Eyebrow>And everything around them</Eyebrow>

                            <RevealText
                                as='h2'
                                id='capabilities-title'
                                text='Built for the whole finance stack.'
                                highlight={['whole finance stack.']}
                                highlightClassName='text-stone'
                                className='mt-6 font-heading text-display-lg font-medium text-burgundy'
                            />
                        </div>

                        <p data-reveal='' className='text-lg leading-relaxed text-stone-dark lg:col-span-4 lg:col-start-9'>
                            The details that make Veyra fit how your company actually runs, from approvals to subsidiaries.
                        </p>
                    </div>

                    <ul className='mt-16 grid gap-px overflow-hidden rounded-[28px] border border-burgundy/10 bg-burgundy/10 sm:grid-cols-2 lg:grid-cols-3'>
                        {CAPABILITIES.map(({ title, body, Icon }, index) => (
                            <li
                                key={title}
                                data-reveal='fade'
                                className='bg-paper p-8 transition-colors duration-300 hover:bg-ivory md:p-10'
                                style={{ '--reveal-delay': `${(index % 3) * 80}ms` } as CSSProperties}
                            >
                                <Icon aria-hidden='true' className='size-5 text-gold-dark' />
                                <h3 className='mt-6 text-lg font-medium text-burgundy'>{title}</h3>
                                <p className='mt-2 leading-relaxed text-stone-dark'>{body}</p>
                            </li>
                        ))}
                    </ul>
                </Container>
            </section>
        </Layout>
    )
}

export default Features
